import {
  getActiveOrganizationId,
  withActiveOrganizationId,
} from '@utils/activeOrganization';
import { forwardRef } from 'react';
import { type LinkProps, useHref, useLinkClickHandler } from 'react-router';

const appendActiveOrganizationId = (target: string): string => {
  const organizationId = getActiveOrganizationId();
  return organizationId
    ? withActiveOrganizationId(target, organizationId)
    : target;
};

// The org id is added only to the rendered href, which is what the browser uses
// when a link is opened in a new tab (middle-click / cmd-click / target=_blank),
// where sessionStorage is not reliably copied. A left-click stays a clean SPA
// navigation to the original target, so the query string never enters the
// address bar and there is no flicker. Mirrors react-router's own Link.
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    to,
    replace,
    state,
    target,
    relative,
    preventScrollReset,
    viewTransition,
    reloadDocument,
    onClick,
    ...rest
  },
  ref,
) {
  const href = useHref(to, { relative });
  const handleClick = useLinkClickHandler(to, {
    replace,
    state,
    target,
    relative,
    preventScrollReset,
    viewTransition,
  });

  return (
    <a
      {...rest}
      ref={ref}
      href={appendActiveOrganizationId(href)}
      target={target}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented && !reloadDocument) {
          handleClick(event);
        }
      }}
    />
  );
});
Link.displayName = 'Link';
